import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Grid, Typography, Box, Button, Alert, Skeleton, Chip, Tabs, Tab,
    Stack, TextField, FormControlLabel, Switch
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Add, CheckCircle, Cancel, Delete } from '@mui/icons-material';
import {
    createFmExistingUser,
    deleteFmExistingUser,
    fetchFmDataProducts,
    fetchFmExistingUsers,
    setFmExistingUserActive
} from '../../lib/services/fmDataService';

const FinalMileAdminPage = ({ idToken, drawerWidth }) => {
    const [dataProducts, setDataProducts] = useState([]);
    const [existingUsers, setExistingUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveMessage, setSaveMessage] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [newUser, setNewUser] = useState({
        userIdentifier: '',
        email: '',
        entraObjectId: ''
    });
    const [userBusy, setUserBusy] = useState(false);
    const navigate = useNavigate();

    const contentOffset = Math.max((drawerWidth || 0) - 15, 0);
    const containerStyle = {
        marginLeft: contentOffset,
        width: `calc(100% - ${contentOffset}px)`,
        boxSizing: 'border-box'
    };

    const loadAdminData = () => {
        if (!idToken) return;
        setLoading(true);
        Promise.all([
            fetchFmDataProducts(idToken, false),
            fetchFmExistingUsers(idToken)
        ])
            .then(([productsResponse, usersResponse]) => {
                if (productsResponse.isError) {
                    setError(productsResponse.data || 'Failed to load data products');
                    return;
                }

                if (usersResponse.isError) {
                    setError(usersResponse.data || 'Failed to load existing users');
                    return;
                }

                setDataProducts((productsResponse.data || []).map((p, i) => ({
                    id: i,
                    productId: p.id,
                    name: p.name,
                    userFriendlyAliasName: p.userFriendlyAliasName || '',
                    description: p.description || '',
                    isEnabled: p.isEnabled,
                    enablePermissionRules: p.enablePermissionRules,
                    inceptionDateTime: p.inceptionDateTime ? new Date(p.inceptionDateTime).toLocaleDateString() : 'N/A',
                    lastRefreshedDateTime: p.lastRefreshedDateTime ? new Date(p.lastRefreshedDateTime).toLocaleDateString() : 'N/A'
                })));

                setExistingUsers((usersResponse.data || []).map((user) => ({
                    id: user.id,
                    userIdentifier: user.userIdentifier,
                    email: user.email || '',
                    entraObjectId: user.entraObjectId || '',
                    source: user.source || '',
                    isActive: user.isActive,
                    firstSeenDateTime: user.firstSeenDateTime ? new Date(user.firstSeenDateTime).toLocaleDateString() : 'N/A',
                    lastSeenDateTime: user.lastSeenDateTime ? new Date(user.lastSeenDateTime).toLocaleDateString() : 'N/A'
                })));

                setError(null);
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAdminData();
    }, [idToken]);

    const columns = [
        {
            field: 'name',
            headerName: 'Data Product Name',
            flex: 2,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'userFriendlyAliasName',
            headerName: 'Alias',
            flex: 1,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 2,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'isEnabled',
            headerName: 'Enabled',
            width: 100,
            headerClassName: 'super-app-theme--header',
            renderCell: (params) => (
                params.value
                    ? <CheckCircle sx={{ color: '#07CF5E' }} />
                    : <Cancel sx={{ color: '#ccc' }} />
            )
        },
        {
            field: 'enablePermissionRules',
            headerName: 'Permissioned',
            width: 130,
            headerClassName: 'super-app-theme--header',
            renderCell: (params) => (
                params.value
                    ? <Chip label="Yes" size="small" color="warning" />
                    : <Chip label="No" size="small" variant="outlined" />
            )
        },
        {
            field: 'inceptionDateTime',
            headerName: 'Inception Date',
            width: 130,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'lastRefreshedDateTime',
            headerName: 'Last Refreshed',
            width: 130,
            headerClassName: 'super-app-theme--header'
        }
    ];

    const handleRowClick = (params) => {
        navigate(`/finalmile-admin/${params.row.productId}`);
    };

    const handleAddProduct = () => {
        navigate('/finalmile-admin/new');
    };

    const handleAddExistingUser = async () => {
        if (!newUser.userIdentifier.trim()) {
            setSaveMessage({ severity: 'error', text: 'User identifier is required.' });
            return;
        }

        setUserBusy(true);
        try {
            const response = await createFmExistingUser(idToken, {
                userIdentifier: newUser.userIdentifier.trim(),
                email: newUser.email.trim() || null,
                entraObjectId: newUser.entraObjectId.trim() || null
            });

            if (response.isError) {
                setSaveMessage({ severity: 'error', text: response.data || 'Failed to add user.' });
                return;
            }

            setNewUser({ userIdentifier: '', email: '', entraObjectId: '' });
            setSaveMessage({ severity: 'success', text: 'Existing user added.' });
            loadAdminData();
        } catch (e) {
            setSaveMessage({ severity: 'error', text: e.message || 'Failed to add user.' });
        } finally {
            setUserBusy(false);
        }
    };

    const handleSetActive = async (id, isActive) => {
        setUserBusy(true);
        try {
            const response = await setFmExistingUserActive(idToken, id, isActive);
            if (response.isError) {
                setSaveMessage({ severity: 'error', text: response.data || 'Failed to update user.' });
                return;
            }

            setSaveMessage({ severity: 'success', text: `User ${isActive ? 'activated' : 'deactivated'}.` });
            loadAdminData();
        } catch (e) {
            setSaveMessage({ severity: 'error', text: e.message || 'Failed to update user.' });
        } finally {
            setUserBusy(false);
        }
    };

    const handleDeleteExistingUser = async (id) => {
        setUserBusy(true);
        try {
            const response = await deleteFmExistingUser(idToken, id);
            if (response.isError) {
                setSaveMessage({ severity: 'error', text: response.data || 'Failed to delete user.' });
                return;
            }

            setSaveMessage({ severity: 'success', text: 'Existing user removed.' });
            loadAdminData();
        } catch (e) {
            setSaveMessage({ severity: 'error', text: e.message || 'Failed to delete user.' });
        } finally {
            setUserBusy(false);
        }
    };

    const existingUserColumns = [
        {
            field: 'userIdentifier',
            headerName: 'User Identifier',
            flex: 1.4,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1.1,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'entraObjectId',
            headerName: 'Entra Object Id',
            flex: 1.2,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'source',
            headerName: 'Source',
            flex: 0.8,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'firstSeenDateTime',
            headerName: 'First Seen',
            width: 120,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'lastSeenDateTime',
            headerName: 'Last Seen',
            width: 120,
            headerClassName: 'super-app-theme--header'
        },
        {
            field: 'isActive',
            headerName: 'Active',
            width: 120,
            headerClassName: 'super-app-theme--header',
            renderCell: (params) => (
                <FormControlLabel
                    control={
                        <Switch
                            checked={params.value}
                            onChange={(event) => handleSetActive(params.row.id, event.target.checked)}
                            disabled={userBusy}
                        />
                    }
                    label={params.value ? 'Yes' : 'No'}
                />
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 110,
            sortable: false,
            filterable: false,
            headerClassName: 'super-app-theme--header',
            renderCell: (params) => (
                <Button
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => handleDeleteExistingUser(params.row.id)}
                    disabled={userBusy}
                >
                    Remove
                </Button>
            )
        }
    ];

    return (
        <Grid container spacing={2} style={containerStyle}>
            <Grid item xs={10}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h5">FinalMile Admin</Typography>
                    <Typography color="text.secondary">
                        Manage data products and the explicit FinalMile access allow-list.
                    </Typography>
                </Box>
            </Grid>
            <Grid item xs={2}>
                {activeTab === 0 && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleAddProduct}
                    >
                        Add Product
                    </Button>
                )}
            </Grid>
            {error && (
                <Grid item xs={12}>
                    <Alert severity="error">{typeof error === 'string' ? error : 'An error occurred.'}</Alert>
                </Grid>
            )}
            {saveMessage && (
                <Grid item xs={12}>
                    <Alert severity={saveMessage.severity} onClose={() => setSaveMessage(null)}>{saveMessage.text}</Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 2 }}>
                    <Tab label="Data Products" />
                    <Tab label="Existing Users" />
                </Tabs>
            </Grid>
            <Grid item xs={12}>
                {loading ? (
                    <Box>
                        <Skeleton variant="rectangular" height={50} sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" height={400} />
                    </Box>
                ) : (
                    activeTab === 0 ? (
                        <Box sx={{
                            height: 650,
                            width: '100%',
                            '& .super-app-theme--header': {
                                backgroundColor: '#A5D7DA',
                                fontWeight: 'bold'
                            }
                        }}>
                            <DataGrid
                                columns={columns}
                                rows={dataProducts}
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 300 }
                                    }
                                }}
                                onRowClick={handleRowClick}
                                pageSizeOptions={[10, 25, 50]}
                                initialState={{
                                    pagination: { paginationModel: { pageSize: 10 } }
                                }}
                                sx={{
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: 'pointer'
                                    }
                                }}
                            />
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            <Box sx={{
                                p: 2,
                                border: '1px solid #d7e3e4',
                                borderRadius: 1,
                                backgroundColor: '#f8fbfb'
                            }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Add Existing User</Typography>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        label="User Identifier"
                                        value={newUser.userIdentifier}
                                        onChange={(event) => setNewUser((previous) => ({ ...previous, userIdentifier: event.target.value }))}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        value={newUser.email}
                                        onChange={(event) => setNewUser((previous) => ({ ...previous, email: event.target.value }))}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Entra Object Id"
                                        value={newUser.entraObjectId}
                                        onChange={(event) => setNewUser((previous) => ({ ...previous, entraObjectId: event.target.value }))}
                                    />
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={handleAddExistingUser}
                                        disabled={userBusy}
                                    >
                                        Add User
                                    </Button>
                                </Stack>
                            </Box>

                            <Box sx={{
                                height: 650,
                                width: '100%',
                                '& .super-app-theme--header': {
                                    backgroundColor: '#A5D7DA',
                                    fontWeight: 'bold'
                                }
                            }}>
                                <DataGrid
                                    columns={existingUserColumns}
                                    rows={existingUsers}
                                    slots={{ toolbar: GridToolbar }}
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 300 }
                                        }
                                    }}
                                    disableRowSelectionOnClick
                                    pageSizeOptions={[10, 25, 50]}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 10 } }
                                    }}
                                />
                            </Box>
                        </Stack>
                    )
                )}
            </Grid>
        </Grid>
    );
};

export default FinalMileAdminPage;
